import { useState, useEffect } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { useNavigate, useLocation } from 'react-router-dom'
// ★★★ Build 35 — `Stethoscope` WAS RENDERED ON LINE 436 AND IMPORTED NOWHERE. It is
//   inside the MD-unread badge, so it crashed only a case that HAS an unread message
//   from its doctor — which is why it survived five builds. `check-jsx-symbols.mjs`,
//   ported from the MD panel, is the gate; this is the same defect as v242.62.0's
//   `<Plus/>` white screen, in a different repo.
import { Plus, X, Trash2, Archive, Calendar, ChevronRight, ChevronDown, FileText, FolderOpen, Users, RotateCcw, Stethoscope, AlertTriangle, RefreshCw } from 'lucide-react'
import { useCaseStore, useUserStore, useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import { useQuestionnaireTranslation } from '../../i18n/questionnaires/useQuestionnaireTranslation'
import LanguageSelector from '../../components/LanguageSelector'
import HamburgerMenu from '../../components/HamburgerMenu'
import OrellaLoader, { OrellaSpinner } from '../../components/OrellaLoader'
import OrellaPageHeader from '../../components/OrellaPageHeader'
import AppHeader from '../../components/AppHeader'
import { useAgent } from '../../components/AgentContext'
import { notificationsAPI } from '../../services/api'
import { formatDate } from '../../utils/dateUtils'
import { getStatusBadge as getSharedStatusBadge } from '../../utils/caseBadges'
import { isTrulyReadyToShare } from '../../utils/caseShareState'
import { formatSpecialtyName, formatCaseSpecialty } from '../../utils/specialtyResolver'
import { isHeldByDoctor } from '../../utils/caseDelete'
import { asText, asFirst } from '../../utils/asText' // PROD-1156: a case-plan value is drawn as text, never as an object

// v206.1: SPECIALTY_KEY_MAP + formatSpecialtyName moved to shared utils/specialtyResolver.js

export default function CasesListScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    if (location.state?.openMenu) { setMenuOpen(true); window.history.replaceState({}, '') }
  }, [location.state])
  const { t, currentLanguage } = useI18n()

  // v220.2 — Map of caseId → unread MD-message count, derived from notifications.
  // Renders the "1 from MD" badge on each case tile.
  const [mdUnreadByCase, setMdUnreadByCase] = useState({})
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const result = await notificationsAPI.list()
        if (cancelled) return
        const counts = {}
        ;(result.notifications || []).forEach((n) => {
          if (n.read) return
          if (n.type !== 'case_message') return
          const cid = n.data?.caseId
          if (!cid) return
          if (n.data?.direction && n.data.direction !== 'from_md') return
          counts[cid] = (counts[cid] || 0) + 1
        })
        setMdUnreadByCase(counts)
      } catch (err) {
        console.log('[CasesList] mdUnread fetch error', err)
      }
    }
    load()
    const onVis = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVis)
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVis) }
  }, [])
  const { tfa } = useQuestionnaireTranslation()
  const cases = useCaseStore((state) => state.cases)
  const casesLoading = useCaseStore((state) => state.casesLoading)
  const loadCases = useCaseStore((state) => state.loadCases)
  const casesLoadFailed = useCaseStore((state) => state.casesLoadFailed)
  const setCurrentCase = useCaseStore((state) => state.setCurrentCase)
  const deleteCase = useCaseStore((state) => state.deleteCase)
  const archiveCase = useCaseStore((state) => state.archiveCase)
  const reactivateCase = useCaseStore((state) => state.reactivateCase)
  const user = useUserStore((state) => state.user)
  const profile = useProfileStore((state) => state.profile)
  const [menuOpen, setMenuOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null) // Case ID to delete
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(null) // Case ID to archive
  const [archiveReason, setArchiveReason] = useState('')
  const [statusFilter, setStatusFilter] = useState('all') // all | active | draft | shared | finalized
  const [timeFilter, setTimeFilter] = useState('30d') // 14d | 30d | 6mo | 12mo | all
  const [showTimeDropdown, setShowTimeDropdown] = useState(false)
  const [showCards, setShowCards] = useState(false)
  const { openAgent } = useAgent()

  const firstName = user?.firstName || profile?.firstName || 'User'
  const lastName = user?.lastName || profile?.lastName || ''

  // Auto-refresh cases on mount + tab/app resume
  useEffect(() => {
    const refreshCases = async () => {
      setIsRefreshing(true)
      try {
        await loadCases()
      } catch (err) {
        console.error('Failed to refresh cases:', err)
      }
      setIsRefreshing(false)
    }
    refreshCases()

    // v163.6.0: Refresh when user returns to tab/app
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadCases().catch((e) => console.error('[CasesList] loadCases failed:', e?.name))
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [])
  // v201.9: Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
    const sw = document.querySelector('.scroll-wrapper')
    if (sw) sw.scrollTop = 0
  }, [])


  // Sort cases by date (newest first) and filter by time + status
  // v212.3: Time filter — default to last 30 days
  const getTimeFilterDate = () => {
    const now = new Date()
    if (timeFilter === '14d') return new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)
    if (timeFilter === '30d') return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    if (timeFilter === '6mo') return new Date(now.setMonth(now.getMonth() - 6))
    if (timeFilter === '12mo') return new Date(now.setFullYear(now.getFullYear() - 1))
    return null // 'all'
  }
  const timeFilterDate = getTimeFilterDate()

  const sortedCases = [...cases]
    .filter(c => {
      // Time filter
      if (timeFilterDate && c.createdAt) {
        const caseDate = new Date(c.createdAt)
        if (caseDate < timeFilterDate) return false
      }
      if (statusFilter === 'all') return true
      const status = c.status
      const isDescribeComplete = c.describeComplete || c.describe?.completed
      const isDraftCase = !isDescribeComplete && !['completed','in_follow_up','in_observation','shared','claimed','scheduled','in_progress','ready_to_share','archived'].includes(status)
      
      if (statusFilter === 'draft') return isDraftCase
      if (statusFilter === 'active') {
        return ['ready_to_share','shared','claimed','scheduled','in_progress'].includes(status) || (isDescribeComplete && !['completed','in_follow_up','in_observation','archived'].includes(status))
      }
      if (statusFilter === 'shared') return status === 'shared'
      if (statusFilter === 'finalized') return ['completed','in_follow_up','in_observation'].includes(status)
      if (statusFilter === 'archived') return status === 'archived'
      return true
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  
  // Status counts for filter badges
  const statusCounts = cases.reduce((acc, c) => {
    const status = c.status
    const isDescribeComplete = c.describeComplete || c.describe?.completed
    const isDraftCase = !isDescribeComplete && !['completed','in_follow_up','in_observation','shared','claimed','scheduled','in_progress','ready_to_share','archived'].includes(status)
    if (isDraftCase) acc.draft++
    else if (['ready_to_share','shared','claimed','scheduled','in_progress'].includes(status) || (isDescribeComplete && !['completed','in_follow_up','in_observation','archived'].includes(status))) acc.active++
    if (['completed','in_follow_up','in_observation'].includes(status)) acc.finalized++
    if (status === 'archived') acc.archived++
    return acc
  }, { draft: 0, active: 0, finalized: 0, archived: 0 })
  
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Handle delete draft
  const handleDeleteDraft = async (caseId) => {
    setDeleteLoading(true)
    try {
      if (deleteCase) {
        await deleteCase(caseId)
      }
      setShowDeleteConfirm(null)
    } catch (err) {
      console.error('[CasesList] Delete failed:', err)
      alert(t('errors2.failedToDelete'))
    } finally {
      setDeleteLoading(false)
    }
  }
  
  // Check if case is a draft (incomplete)
  const isDraft = (caseItem) => {
    const isDescribeComplete = caseItem.describeComplete || caseItem.describe?.completed
    const status = caseItem.status
    return !isDescribeComplete && status !== 'completed' && status !== 'in_follow_up' && status !== 'in_observation' && status !== 'shared' && status !== 'claimed' && status !== 'scheduled' && status !== 'ready_to_share' && status !== 'archived'
  }
  
  // Check if case can be archived (submitted but not shared/claimed/completed/in_follow_up/already archived)
  const canArchive = (caseItem) => {
    const isDescribeComplete = caseItem.describeComplete || caseItem.describe?.completed
    const status = caseItem.status
    const isShared = caseItem.isShared || caseItem.shareCode || status === 'shared' || status === 'claimed' || status === 'scheduled' || status === 'in_follow_up' || status === 'in_observation'
    // Can archive if: describe is complete AND not shared AND not claimed AND not completed AND not in_follow_up AND not already archived
    return isDescribeComplete && !isShared && status !== 'completed' && status !== 'in_follow_up' && status !== 'in_observation' && status !== 'archived'
  }
  
  // Handle archive case
  const handleArchiveCase = async (caseId) => {
    if (archiveCase) {
      await archiveCase(caseId, archiveReason || t('casesList.unknown'))
    }
    setShowArchiveConfirm(null)
    setArchiveReason('')
  }

  // formatDate imported from dateUtils

  // ═══ v206.0: getStatusBadge moved to shared utils/caseBadges.js so HomeScreen + CasesListScreen stay in sync ═══
  const getStatusBadge = (caseItem) => getSharedStatusBadge(caseItem, t)

  // Secondary badge — shown alongside primary badge for additional context
  // v221.2.0: referral chrome was violet (#EDE9FE/#6D28D9) — not in canonical
  // palette. Collapsed to Light Blue + Navy chip to match referral-on-case
  // treatment elsewhere.
  const SECONDARY_BADGES = {
    completed:      { label: t('casesList.complete'),     bg: '#D5EFD3', color: '#15803D' },
    finalized:      { label: t('casesList.complete'),     bg: '#D5EFD3', color: '#15803D' },
    referral:       { label: t('casesList.referral'),     bg: '#A7D9FC', color: '#15354F' },
  }

  const getFollowUpBadge = (caseItem) => {
    const st = caseItem.status
    if (!['completed', 'referral'].includes(st)) return null
    
    if (st === 'referral') {
      const specialty = asFirst(caseItem.followUp?.referral?.specialty, caseItem.diagnose?.followUp?.referral?.specialty) // PROD-1156
      const badge = { ...SECONDARY_BADGES.referral }
      if (specialty) badge.label = t('cases.referredSpecialty', { specialty })
      return badge
    }

    return SECONDARY_BADGES[st] || null
  }

  const handleCaseClick = (caseItem) => {
    setCurrentCase(caseItem)
    
    // Check if this is an MD-initiated case that needs onboarding
    const isMDInitiated = caseItem.doctor || caseItem.doctor_id || caseItem.sentToDoctor || 
      (caseItem.status === 'claimed' && !caseItem.describeComplete)
    const hasCompletedOnboarding = caseItem.patientAgreement?.signed || caseItem.onboardingComplete
    
    if (isMDInitiated && !hasCompletedOnboarding) {
      console.log('[CasesListScreen] MD-initiated case needs onboarding, redirecting to share screen')
      navigate('/case/share', { state: { caseId: caseItem.id, mdOnboard: true } })
    } else {
      navigate('/case/home', { state: { returnTo: '/cases' } })
    }
  }

  // ★★ v242.98.0 (TF b37 #2, second half) — "If a case is truly 'Ready to Share'
  //   let's make that pill a CTA to flag patient to share it / pick up share
  //   sequence." Same setCurrentCase → navigate pair handleCaseClick uses, so the
  //   share screen receives the case the same way however the patient got there.
  const handleShareClick = (e, caseItem) => {
    e.stopPropagation()   // the whole row is a button; this must not also open Case Home
    setCurrentCase(caseItem)
    navigate('/case/share', { state: { caseId: caseItem.id, returnTo: '/cases' } })
  }

  // v159.1: Show loading spinner while cases are loading (not just initial)
  if (casesLoading) {
    return (
      <div className="min-h-full bg-white flex flex-col">
        <OrellaLoader message={t('casesList.loadingCases')} />
      </div>
    )
  }

  return (
    <div className="min-h-full bg-white flex flex-col relative">
      {/* Header — shared navy pattern (v242.16.0), carries to the top via NAVY_HEADER_ROUTES */}
      <AppHeader
        icon={FolderOpen}
        title={t('cases.title')}
        onMenu={() => setMenuOpen(true)}
        onLanguage={() => setLanguageOpen(true)}
        meta={sortedCases.length > 0
          ? `${sortedCases.length} ${sortedCases.length === 1 ? t('casesList.caseCount') : t('casesList.casesCount')}`
          : undefined}
      />

      {/* Orella Dialogue — below heading, above filters */}
      <OrellaPageHeader
        message={sortedCases.length === 0
          ? t('casesList.noCasesYet')
          : t('casesList.hereCases')
        }
        sessionKey="cases_list"
        chips={[
          { label: t('casesList.askOrella'), onClick: () => openAgent() },
        ]}
        onAnimationDone={() => setShowCards(true)}
      />
      
      {/* Time + Status Filter — gated behind Orella animation */}
      <div className={`transition-all duration-500 ${showCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
      <div className="px-4 pt-2 pb-1 bg-white">
        {/* Time filter dropdown + Status filter row */}
        <div className="flex gap-1.5 mb-2 items-center flex-wrap">
          {/* Time dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowTimeDropdown(!showTimeDropdown)}
              className="px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap flex items-center gap-1 transition-colors"
              style={{ backgroundColor: '#46A8F9', color: 'white' }}
            >
              <Calendar size={11} />
              {{ '14d': '2 Weeks', '30d': '30 Days', '6mo': '6 Months', '12mo': '12 Months', 'all': 'All Time' }[timeFilter]}
              <ChevronDown size={11} />
            </button>
            {showTimeDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowTimeDropdown(false)} />
                <div className="absolute top-[calc(100%+4px)] left-0 z-20 bg-white rounded-xl py-1 border border-slate-200 min-w-[130px]"
                  style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                >
                  {[
                    { key: '14d', label: '2 Weeks' },
                    { key: '30d', label: '30 Days' },
                    { key: '6mo', label: '6 Months' },
                    { key: '12mo', label: '12 Months' },
                    { key: 'all', label: 'All Time' },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => { setTimeFilter(f.key); setShowTimeDropdown(false) }}
                      className={`block w-full text-left px-3.5 py-2 text-xs ${
                        timeFilter === f.key ? 'font-semibold text-orella-500 bg-orella-50' : 'text-slate-600'
                      }`}
                    >{f.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
          {/* Status filter pills — same row */}
          {[
            { key: 'all', label: t('casesList.all'), count: cases.length },
            { key: 'active', label: t('casesList.active'), count: statusCounts.active },
            { key: 'draft', label: t('casesList.drafts'), count: statusCounts.draft },
            { key: 'finalized', label: t('casesList.finalized'), count: statusCounts.finalized },
            { key: 'archived', label: t('casesList.archived'), count: statusCounts.archived },
          ].filter(f => f.key === 'all' || f.count > 0).map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap transition-colors flex items-center gap-1.5"
              style={statusFilter === f.key 
                ? { backgroundColor: '#15354F', color: 'white' } 
                : { border: '1px solid #E2E8F0', color: '#64748B' }
              }
            >
              {f.label}
              {f.count > 0 && statusFilter !== f.key && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#F1F5F9', color: '#94A3B8' }}>{f.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Cases List */}
      <div className="flex-1 px-4 pb-24">
        {/* PROD-1412: the list answered 503 case_list_unavailable. Cached cases stay below; Try again re-calls the loader. */}
        {casesLoadFailed && (
          <div className="rounded-xl p-3 mb-3 flex flex-col items-start gap-2 text-[12.5px]" style={{ background: '#FFF7E8', border: '1px solid #F5D9A8', color: '#15354F' }} data-testid="cases-load-failed" role="alert">
            <span className="flex items-start gap-2">
              <AlertTriangle size={14} className="flex-none mt-0.5" style={{ color: '#B45309' }} />
              <span>{t('casesList.loadFailed', 'We could not load your cases.')}</span>
            </span>
            <button type="button" onClick={() => { loadCases().catch(() => {}) }} data-cta="cases-retry" disabled={casesLoading} className="text-[13px] font-bold flex items-center gap-1" style={{ color: '#46A8F9' }}>
              <RefreshCw size={12} /> {t('common.tryAgain', 'Try again')}
            </button>
          </div>
        )}
        {sortedCases.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <FileText className="text-gray-400" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('cases.noCases')}</h3>
            <p className="text-gray-500 text-sm mb-6">
              {t('cases.noCasesHint')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedCases.map((caseItem) => {
              const status = getStatusBadge(caseItem)
              // ★ Only a case with NO share code and NO doctor is truly waiting on
              //   the patient. A code-shared case now reads "Shared" and must not
              //   be nagged to share again — that was the whole of TF b37 #2.
              const readyToShareCta = isTrulyReadyToShare(caseItem)
              const followUpBadge = getFollowUpBadge(caseItem)
              const caseIsDraft = isDraft(caseItem)
              const specialtyRaw = caseItem.specialtyName || caseItem.specialty?.name || t('cases.unknown')
              const focusAreaRaw = caseItem.focusAreaName || caseItem.focusArea?.name || t('cases.casePrefix')
              const specialtyName = formatCaseSpecialty(caseItem, t)
              const focusAreaName = tfa(focusAreaRaw)
              
              // Accent bar color based on status
              // v221.2.0: canonicalized to brand palette. in_follow_up was
              // status-amber (#F59E0B) — flagged on patient app; follow-up is
              // a workflow phase, not an attention signal. All workflow phases
              // now use brand Blue (#46A8F9). Completed stays brand Green;
              // observation/archived stay slate neutrals.
              const accentColor = (() => {
                const s = caseItem.status
                if (s === 'completed') return '#66D170'                  // Brand Green
                if (s === 'in_observation') return '#64748B'             // Slate (neutral, observation = monitoring)
                if (s === 'archived') return '#94A3B8'                   // Slate light
                // Active workflow phases — all brand Blue
                if (s === 'in_follow_up') return '#46A8F9'
                if (s === 'scheduled') return '#46A8F9'
                if (s === 'claimed' || s === 'in_progress') return '#46A8F9'
                if (s === 'shared') return '#46A8F9'
                if (s === 'ready_to_share') return '#46A8F9'
                if (caseItem.describeComplete || caseItem.describe?.completed) return '#46A8F9'
                return '#CBD5E1'                                          // Pre-active draft state
              })()
              
              // Status pill style (soft bg + colored text instead of solid)
              // v152.0.8: Badges return { label, bg, color } directly — no colorMap needed
              const pillStyle = status
              const followPillStyle = followUpBadge

              return (
                <div
                  key={caseItem.id}
                  className="w-full bg-white rounded-[14px] overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  {/* Top accent bar */}
                  <div className="h-[3px]" style={{ background: accentColor }} />
                  
                  <div className="flex">
                    <button
                      onClick={() => handleCaseClick(caseItem)}
                      className="flex-1 p-3.5 text-left min-w-0"
                    >
                      {/* Row 1: Case ID + Status pills */}
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[13px] font-bold tracking-wide" style={{ color: '#15354F' }}>
                          {caseItem.caseNumber?.startsWith('#') ? caseItem.caseNumber : `#${caseItem.caseNumber || caseItem.id?.slice(-8).toUpperCase()}`}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {/* ★★★ v242.98.0 — A `<button>` MAY NOT NEST A `<button>`
                              (ORELLA_RULES §1bf(e)): React renders it and the browser
                              un-nests it at parse time, which would tear this pill out
                              of the row. The row is already a button, so the CTA is a
                              span carrying role/tabIndex/keyboard rather than a real
                              one — and it stopPropagation()s so tapping it opens the
                              share flow instead of Case Home.
                              ★ The arrow is not decoration. §1bh(a): an affordance is
                                what the SHAPE promises, so only the tappable variant
                                gets one, and every other status stays an inert span. */}
                          {readyToShareCta ? (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => handleShareClick(e, caseItem)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleShareClick(e, caseItem) }}
                              className="text-[10px] font-bold px-2 py-[3px] rounded-[6px] inline-flex items-center gap-1 cursor-pointer active:opacity-80"
                              style={{ backgroundColor: '#15354F', color: '#FFFFFF' }}
                              aria-label={t('casesList.shareThisCase', 'Share this case')}
                            >
                              {t('casesList.shareNow', 'Share now')}
                              <ChevronRight size={11} strokeWidth={3} />
                            </span>
                          ) : (
                            <span 
                              className="text-[10px] font-semibold px-2 py-[3px] rounded-[6px]"
                              style={{ backgroundColor: pillStyle.bg, color: pillStyle.color }}
                            >
                              {status.label}
                            </span>
                          )}
                          {followUpBadge && followPillStyle && (
                            <span 
                              className="text-[10px] font-semibold px-2 py-[3px] rounded-[6px]"
                              style={{ backgroundColor: followPillStyle.bg, color: followPillStyle.color }}
                            >
                              {followUpBadge.label}
                            </span>
                          )}
                          {/* v220.2 — MD message badge */}
                          {mdUnreadByCase[caseItem.id] > 0 && (
                            <span
                              className="text-[10px] font-semibold px-2 py-[3px] rounded-[6px] inline-flex items-center gap-1"
                              style={{ backgroundColor: '#D5EFD3', color: '#16A34A' }}
                            >
                              <Stethoscope size={9} strokeWidth={2.5} />
                              {mdUnreadByCase[caseItem.id]} {t('cases.fromMd') || 'from MD'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Row 2: Specialty > Focus */}
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[16px] font-bold" style={{ color: '#15354F' }}>{specialtyName}</span>
                        <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="text-[16px] font-bold truncate" style={{ color: '#15354F' }}>{focusAreaName}</span>
                      </div>
                      
                      {/* Row 3: Date + Doctor */}
                      <div className="flex items-center gap-2 text-[11px] text-gray-400">
                        <div className="flex items-center gap-1">
                          <Calendar size={11} />
                          <span>{formatDate(caseItem.createdAt)}</span>
                        </div>
                        {caseItem.sentToDoctorName && (
                          <>
                            <span>·</span>
                            <span className="text-[#46A8F9] font-medium">{caseItem.sentToDoctorName}</span>
                          </>
                        )}
                      </div>
                      {/* v211.7: Referral context badge */}
                      {caseItem.originatorType === 'md_referral' && caseItem.referralOriginatorName && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#E9F8FF', color: '#15354F' }}>
                            <Users size={10} />
                            {t('cases.referredBy') || 'Referred by'} {caseItem.referralOriginatorName}
                          </span>
                        </div>
                      )}
                    </button>
                    
                    {/* Action buttons */}
                    <div className="flex items-center gap-1 pr-2 flex-shrink-0">
                      {caseItem.status === 'archived' && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation()
                            const res = await reactivateCase(caseItem.id)
                            if (res?.success !== false) {
                              // PROD-965: the store's reactivated case, not the stale archived caseItem.
                              setCurrentCase(caseItem.id)
                              navigate('/case/share')
                            }
                          }}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-semibold rounded-lg transition-colors"
                          style={{ color: '#15354F', backgroundColor: '#E9F8FF' }}
                          title={t('casesList.reactivateAndShare', 'Reactivate & share')}
                        >
                          <RotateCcw size={14} />
                          {t('casesList.reactivate', 'Reactivate')}
                        </button>
                      )}
                      {canArchive(caseItem) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowArchiveConfirm(caseItem.id)
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t('caseHome.archiveCase') || 'Archive case'}
                        >
                          <Archive size={16} />
                        </button>
                      )}
                      {/* ★ PROD-416: no Delete on a draft a physician holds */}
                      {caseIsDraft && !isHeldByDoctor(caseItem) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowDeleteConfirm(caseItem.id)
                          }}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title={t('casesList.deleteDraft')}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Start New Case button — flows after last case card */}
        <div className="pt-3 pb-4">
          <button
            onClick={() => navigate('/home')}
            className="w-11 h-11 rounded-full flex items-center justify-center text-white shadow-lg active:scale-90 transition-transform"
            style={{ backgroundColor: '#46A8F9', boxShadow: '0 4px 14px rgba(70,168,249,0.4)' }}
          >
            <Plus size={22} strokeWidth={2.5} />
          </button>
        </div>
      </div>
      </div>

      {/* Hamburger Menu */}
      <HamburgerMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Delete Draft Confirmation Modal */}
      {showDeleteConfirm && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <button onClick={() => setShowDeleteConfirm(null)} className="text-gray-400">
                <X size={24} />
              </button>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('caseHome.deleteCase') || 'Delete Draft'}</h3>
            <p className="text-gray-600 mb-6">{t('caseHome.deleteCaseConfirm') || 'This will permanently delete this draft case. This action cannot be undone.'}</p>
            <div className="space-y-3">
              <button
                onClick={() => handleDeleteDraft(showDeleteConfirm)}
                disabled={deleteLoading}
                className="w-full py-3 bg-red-500 text-white rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {deleteLoading ? (
                  <><OrellaSpinner size={16} tone="inherit" /> {t('casesList.deleting', 'Deleting...')}</>
                ) : (
                  t('caseHome.deleteCase') || 'Delete Case'
                )}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-full font-semibold"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
        </OverlayPortal>
      )}

      {/* Archive Case Confirmation Modal */}
      {showArchiveConfirm && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Archive className="text-gray-600" size={24} />
              </div>
              <button onClick={() => { setShowArchiveConfirm(null); setArchiveReason(''); }} className="text-gray-400">
                <X size={24} />
              </button>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('caseHome.archiveCaseTitle') || 'Archive This Case?'}</h3>
            <p className="text-gray-600 mb-4">{t('caseHome.archiveCaseDesc') || 'Please select a reason for archiving this case:'}</p>
            
            {/* Archive reason options */}
            <div className="space-y-2 mb-4">
              <button
                onClick={() => setArchiveReason('resolved_own')}
                className={`w-full py-3 px-4 rounded-xl text-left border-2 transition-colors ${
                  archiveReason === 'resolved_own' ? 'border-orella-500 bg-orella-50' : 'border-gray-200'
                }`}
              >
                {t('caseHome.archiveReasonResolved') || 'Resolved on its own'}
              </button>
              <button
                onClick={() => setArchiveReason('resolved_treatment')}
                className={`w-full py-3 px-4 rounded-xl text-left border-2 transition-colors ${
                  archiveReason === 'resolved_treatment' ? 'border-orella-500 bg-orella-50' : 'border-gray-200'
                }`}
              >
                {t('caseHome.archiveReasonTreatment') || 'Resolved with treatment'}
              </button>
              <button
                onClick={() => setArchiveReason('other')}
                className={`w-full py-3 px-4 rounded-xl text-left border-2 transition-colors ${
                  archiveReason === 'other' ? 'border-orella-500 bg-orella-50' : 'border-gray-200'
                }`}
              >
                {t('caseHome.archiveReasonOther') || 'Other reason'}
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => handleArchiveCase(showArchiveConfirm)}
                disabled={!archiveReason}
                className="w-full py-3 bg-orella-500 text-white rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('caseHome.archiveAndClose') || 'Archive & Close'}
              </button>
              <button
                onClick={() => { setShowArchiveConfirm(null); setArchiveReason(''); }}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-full font-semibold"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
        </OverlayPortal>
      )}

      {/* Language Selector (for header button) */}
      <LanguageSelector isOpen={languageOpen} onClose={() => setLanguageOpen(false)} />
    </div>
  )
}
